import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Receipt, Calendar, User, Users as UsersIcon, Edit2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import type { Expense } from '@/ui/view-props';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useExpenses, useCreateExpense, useDeleteExpense, useUpdateExpense } from '@/hooks/use-expenses';
import { usePlayers } from '@/hooks/use-players';
import { useSessions } from '@/hooks/use-sessions';

export default function Expenses() {
  const { user } = useAuth();
  const { targetUserId, isReadOnly } = useViewMode();
  
  const { data: expenses = [], isLoading: expensesLoading } = useExpenses(targetUserId);
  const { data: players = [], isLoading: playersLoading } = usePlayers(targetUserId);
  const { data: sessions = [], isLoading: sessionsLoading } = useSessions(targetUserId);

  const createExpenseMutation = useCreateExpense();
  const deleteExpenseMutation = useDeleteExpense();
  const updateExpenseMutation = useUpdateExpense();

  const loading = expensesLoading || playersLoading || sessionsLoading;
  
  // Form state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [payerId, setPayerId] = useState('');
  const [sessionId, setSessionId] = useState<string>('none');
  const [category, setCategory] = useState<string>('Other');
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleSessionChange = (sId: string) => {
    setSessionId(sId);
    if (sId !== 'none') {
      const selectedSession = sessions.find(s => s.id === sId);
      if (selectedSession?.session_players) {
        const playerIds = selectedSession.session_players.map((sp: any) => sp.player_id);
        setSelectedParticipants(playerIds);
      }
    }
  };

  const EXPENSE_CATEGORIES = [
    { value: 'Food', icon: '🍕' },
    { value: 'Drinks', icon: '🍺' },
    { value: 'Venue', icon: '🏠' },
    { value: 'Travel', icon: '🚗' },
    { value: 'Other', icon: '📦' },
  ];

  const getCategoryIcon = (cat?: string) => {
    return EXPENSE_CATEGORIES.find(c => c.value === cat)?.icon ?? '📦';
  };

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !amount || !payerId || selectedParticipants.length === 0) {
      return;
    }

    const amountNum = parseFloat(amount);
    // Round share to 2 decimal places to ensure consistent currency values
    const shareAmount = Math.round((amountNum / selectedParticipants.length) * 100) / 100;

    const expenseData = {
      amount: amountNum,
      description: description.trim() || null,
      payer_id: payerId,
      session_id: sessionId === 'none' ? null : sessionId,
      category: category || 'Other',
      created_by: user.id,
    };

    const participantData = selectedParticipants.map(pid => ({
      player_id: pid,
      share: shareAmount,
    }));

    if (editingExpenseId) {
      await updateExpenseMutation.mutateAsync({
        id: editingExpenseId,
        expense: expenseData,
        participants: participantData,
      });
    } else {
      await createExpenseMutation.mutateAsync({
        expense: expenseData,
        participants: participantData,
      });
    }

    setIsAddModalOpen(false);
    resetForm();
  };

  const handleEditClick = (expense: any) => {
    setEditingExpenseId(expense.id);
    setAmount(expense.amount.toString());
    setDescription(expense.description || '');
    setPayerId(expense.payer_id);
    setSessionId(expense.session_id || 'none');
    setCategory(expense.category || 'Other');
    setSelectedParticipants(expense.participants?.map((p: any) => p.player_id) || []);
    setIsAddModalOpen(true);
  };

  const resetForm = () => {
    setEditingExpenseId(null);
    setAmount('');
    setDescription('');
    setPayerId('');
    setSessionId('none');
    setCategory('Other');
    setSelectedParticipants([]);
  };

  const deleteExpense = async () => {
    if (!deleteTargetId || !targetUserId) return;
    await deleteExpenseMutation.mutateAsync({ id: deleteTargetId, userId: targetUserId });
    setDeleteTargetId(null);
  };

  const toggleParticipant = (pid: string) => {
    setSelectedParticipants(prev => 
      prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]
    );
  };

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">Expenses</h1>
        {!isReadOnly && (
          <Dialog open={isAddModalOpen} onOpenChange={(open) => {
            setIsAddModalOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{editingExpenseId ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddExpense} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    placeholder="e.g. Pizza, Drinks, Venue"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Paid By</Label>
                  <Select value={payerId} onValueChange={setPayerId} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select player" />
                    </SelectTrigger>
                    <SelectContent>
                      {players.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Session (Optional)</Label>
                  <Select value={sessionId} onValueChange={handleSessionChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="No session" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No session</SelectItem>
                      {sessions.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {new Date(s.date).toLocaleDateString()} - {s.status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.icon} {c.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Participants (Split equally)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto p-1 border rounded-md">
                    {players.map(p => (
                      <div key={p.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`p-${p.id}`} 
                          checked={selectedParticipants.includes(p.id)}
                          onCheckedChange={() => toggleParticipant(p.id)}
                        />
                        <Label htmlFor={`p-${p.id}`} className="text-sm cursor-pointer truncate">{p.name}</Label>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSelectedParticipants(players.map(p => p.id))}
                    >
                      Select All
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSelectedParticipants([])}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={createExpenseMutation.isPending || updateExpenseMutation.isPending}>
                    {createExpenseMutation.isPending || updateExpenseMutation.isPending ? 'Saving...' : (editingExpenseId ? 'Update Expense' : 'Save Expense')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading && expenses.length === 0 ? (
        <div className="py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <Receipt className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">No expenses recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {expenses.map((expense) => (
            <Card key={expense.id} className="border-0 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg" title={(expense as any).category ?? 'Other'}>{getCategoryIcon((expense as any).category)}</span>
                      <p className="font-bold text-lg">{formatCurrency(expense.amount)}</p>
                      {expense.is_settled && (
                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">Settled</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-foreground truncate">
                      {expense.description || 'No description'}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>Paid by {expense.payer?.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <UsersIcon className="h-3 w-3" />
                        <span>{expense.participants?.length} participants</span>
                      </div>
                      {expense.session_id && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>Session {(expense as any).session?.date ? new Date((expense as any).session.date).toLocaleDateString() : 'N/A'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isReadOnly && !expense.is_settled && (
                    <div className="flex flex-col gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground shrink-0"
                        onClick={() => handleEditClick(expense)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive shrink-0"
                        onClick={() => setDeleteTargetId(expense.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="bg-muted/30 px-4 py-2 border-t border-border/50">
                  <p className="text-[10px] uppercase font-bold text-muted-foreground mb-1">Participants</p>
                  <div className="flex flex-wrap gap-1">
                    {expense.participants?.map(p => (
                      <span key={p.id} className="text-[10px] bg-background border border-border px-1.5 py-0.5 rounded">
                        {p.player?.name} ({formatCurrency(p.share)})
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => { if (!open) setDeleteTargetId(null); }}
        title="Delete Expense"
        description="Are you sure you want to delete this expense? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={deleteExpense}
      />
    </div>
  );
}
